import React, { useState } from 'react';
import * as Scrivito from 'scrivito';
import { requestInquiryTab } from '../../lib/inquiryTab';
import { pageLang, pick } from '../../lib/localize';

// Build attributes for 20 service cells.
// cellTitle_1..20 → stored as cell_title_1..20 (matches Ruby gem)
// cellContent_1..20 → stored as cell_content_1..20
const cellAttributes = {};
for (let i = 1; i <= 20; i++) {
  cellAttributes[`cellTitle_${i}`] = 'string';
  cellAttributes[`cellContent_${i}`] = 'string';
}

Scrivito.provideWidgetClass('OurServicesWidget', {
  attributes: {
    sectionName: 'string',
    title: 'string',
    includeInNav: ['enum', { values: ['ja', 'nein'] }],
    titleToLink: 'string',
    linkTarget: 'string',
    ...cellAttributes,
  },
});

// Icon filename for each cell (1-based index matches cell number)
const CELL_ICONS = {
  1: 'aerospace', 2: 'architecture', 3: 'automotive', 4: 'chemistry', 5: 'environmental',
  6: 'medicine', 7: 'power', 8: 'mechanical_eng', 9: 'electronics', 10: 'household',
  11: 'patent', 12: 'food', 13: 'manufacturing', 14: 'building', 15: 'sanitary',
  16: 'business', 17: 'film', 18: 'energy', 19: 'industrial', 20: 'web',
};

const ALL_CELLS = Array.from({ length: 20 }, (_, idx) => idx + 1);

function ServiceCard({ widget, i, open, onToggle, linkTarget, linkText }) {
  const bodyId = `tt-service-body-${i}`;

  return (
    <div className={`tt-service-card${open ? ' tt-service-card--open' : ''}`}>
      <button
        type="button"
        className="tt-service-card__head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        {CELL_ICONS[i] && (
          <span className="tt-service-card__icon" aria-hidden="true">
            <span
              className="tt-service-card__icon-glyph"
              style={{
                WebkitMaskImage: `url(/icons/${CELL_ICONS[i]}.png)`,
                maskImage: `url(/icons/${CELL_ICONS[i]}.png)`,
              }}
            />
          </span>
        )}
        {/* h3, not h4: the section title above is an h2 and nothing else sits
            between it and these cards, so axe's heading-order check flagged
            the skip. */}
        <h3 className="tt-service-card__title">
          <Scrivito.ContentTag tag="span" attribute={`cellTitle_${i}`} content={widget} />
        </h3>
        <span className="tt-service-card__chevron" aria-hidden="true">
          <i className="ti-angle-down" />
        </span>
      </button>
      <div className="tt-service-card__body" id={bodyId}>
        <div>
          <div className="tt-service-card__content">
            <Scrivito.ContentTag tag="div" attribute={`cellContent_${i}`} content={widget} />
            {/* Every card shares the same request-a-quote link target, so a
                single id (as the Rails markup gave all twenty buttons) would
                be invalid HTML — the tab switch is requested directly instead. */}
            <a
              href={`#${linkTarget}`}
              className="tt-service-card__cta"
              onClick={() => requestInquiryTab('inquiry')}
            >
              {linkText} <i className="ti-arrow-right" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const OurServicesWidget = Scrivito.connect(({ widget }) => {
  const sectionName = widget.get('sectionName');
  const linkTarget = widget.get('linkTarget') || 'request_information';
  const linkText = widget.get('titleToLink') || pick({ de: 'Angebot anfordern', en: 'Request quote' }, pageLang());

  const [openSet, setOpenSet] = useState(() => new Set());
  const toggle = (i) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <section className="tt-services">
      <a className="anchor" id={sectionName || undefined} />
      <div className="container">
        <div className="tt-services__head">
          <h2>
            <Scrivito.ContentTag tag="span" attribute="title" content={widget} />
          </h2>
        </div>
        <div className="tt-services__grid">
          {ALL_CELLS.map((i) => (
            <ServiceCard
              key={i}
              widget={widget}
              i={i}
              open={openSet.has(i)}
              onToggle={() => toggle(i)}
              linkTarget={linkTarget}
              linkText={linkText}
            />
          ))}
        </div>
      </div>
    </section>
  );
});

Scrivito.provideComponent('OurServicesWidget', OurServicesWidget);

// Build cell attribute configs dynamically for cells 1–20
const CELL_ICON_NAMES = {
  1: 'Aerospace', 2: 'Architecture', 3: 'Automotive', 4: 'Chemistry', 5: 'Environmental',
  6: 'Medicine', 7: 'Power', 8: 'Mechanical Engineering', 9: 'Electronics', 10: 'Household',
  11: 'Patent', 12: 'Food', 13: 'Manufacturing', 14: 'Building', 15: 'Sanitary',
  16: 'Business', 17: 'Film', 18: 'Energy', 19: 'Industrial', 20: 'Web',
};

const cellEditingAttributes = {};
for (let i = 1; i <= 20; i++) {
  const name = CELL_ICON_NAMES[i] || `Service ${i}`;
  cellEditingAttributes[`cellTitle_${i}`]   = { title: `${name} – Title` };
  cellEditingAttributes[`cellContent_${i}`] = { title: `${name} – Description` };
}

// Build cell property names list for propertiesGroups
const cellPropertyNames = [];
for (let i = 1; i <= 20; i++) {
  cellPropertyNames.push(`cellTitle_${i}`, `cellContent_${i}`);
}

Scrivito.provideEditingConfig('OurServicesWidget', {
  title: 'Our Services',
  propertiesGroups: [
    {
      title: 'Section Settings',
      properties: ['sectionName', 'title', 'includeInNav', 'titleToLink', 'linkTarget'],
    },
    {
      title: 'Service Cells',
      properties: cellPropertyNames,
    },
  ],
  attributes: {
    sectionName:  { title: 'Section ID', description: 'Used for anchor links (no spaces).' },
    title:        { title: 'Section Heading' },
    includeInNav: {
      title: 'Show in Navigation',
      values: [
        { value: 'ja',   title: 'Yes' },
        { value: 'nein', title: 'No' },
      ],
    },
    titleToLink: { title: '"Request Quote" Button Label' },
    linkTarget:  { title: '"Request Quote" Link Target', description: 'Anchor ID to scroll to (e.g. "request_information").' },
    ...cellEditingAttributes,
  },
  initialContent: {
    includeInNav: 'ja',
    titleToLink: 'Request Quote',
    linkTarget: 'request_information',
  },
});

export default OurServicesWidget;
